import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, Users, Lock, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import { format, addDays, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const SAMPLE_SCHEDULE = [
  { name: 'Reformer Fondations', time: '09:00', end: '10:00', spots: 6 },
  { name: 'Reformer Évolution', time: '10:00', end: '11:00', spots: 6 },
  { name: 'Reformer Évolution', time: '18:00', end: '19:00', spots: 6 },
  { name: 'Reformer Intensité', time: '19:00', end: '20:00', spots: 6 },
];

const SAT_SCHEDULE = [
  { name: 'Reformer Fondations', time: '09:00', end: '10:00', spots: 6 },
  { name: 'Reformer Évolution', time: '10:00', end: '11:00', spots: 6 },
];

export default function Reserver() {
  const [weekStart] = useState(() => {
    const sept1 = new Date(2026, 8, 1); // Sept 1, 2026
    return startOfWeek(sept1, { weekStartsOn: 1 });
  });
  const [selectedDay, setSelectedDay] = useState(0); // Monday

  const weekDays = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i)).filter((d) => d.getDay() !== 3); // Mon-Sat, mercredi masqué
  const isSaturday = weekDays[selectedDay]?.getDay() === 6;
  const schedule = isSaturday ? SAT_SCHEDULE : SAMPLE_SCHEDULE;

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      {/* Header */}
      <div className="gradient-cream py-10 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99A55] mb-2 font-semibold">Réservation</p>
          <h1 className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight" data-testid="reserver-title">
            Réserver une séance
          </h1>
          <div className="divider-premium" />
          <p className="text-sm text-[#7A6455]">6 places maximum par séance</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Locked banner */}
        <Card className="bg-[#493329] text-white rounded-2xl mb-8" data-testid="booking-locked-banner">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#C99A55]/20 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-[#C99A55]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="font-heading font-medium text-lg">Ouverture septembre 2026</p>
                <p className="text-white/50 text-sm">Les réservations seront disponibles dès l'ouverture du studio. Voici un aperçu du planning type.</p>
              </div>
              <Link to="/contact" className="flex-shrink-0">
                <Button size="sm" className="bg-[#C99A55] hover:bg-[#C99A55]/80 text-white rounded-full text-xs btn-premium">
                  <Bell className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Être informée
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Week Navigation (static) */}
        <div className="flex items-center justify-between mb-5">
          <Button variant="ghost" disabled className="text-[#E9DDCC]">
            <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <p className="font-heading font-medium text-sm text-[#493329]">
            {format(weekStart, 'd MMMM', { locale: fr })} — {format(addDays(weekStart, 5), 'd MMMM yyyy', { locale: fr })}
          </p>
          <Button variant="ghost" disabled className="text-[#E9DDCC]">
            <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
          </Button>
        </div>

        {/* Day Selector */}
        <div className="flex gap-1.5 mb-8 overflow-x-auto pb-2">
          {weekDays.map((day, i) => (
            <button
              key={i}
              onClick={() => setSelectedDay(i)}
              className={`flex-1 min-w-[56px] py-3 rounded-2xl text-center transition-all ${
                selectedDay === i
                  ? 'bg-[#C99A55] text-white shadow-md'
                  : 'bg-white border border-[#E9DDCC] text-[#493329] hover:border-[#C99A55]'
              }`}
            >
              <p className="text-[10px] uppercase tracking-wider">{format(day, 'EEE', { locale: fr })}</p>
              <p className="text-lg font-heading">{format(day, 'd')}</p>
            </button>
          ))}
        </div>

        {/* Selected day label */}
        <div className="mb-5">
          <h2 className="font-heading font-medium text-lg text-[#493329]">
            {format(weekDays[selectedDay], 'EEEE d MMMM', { locale: fr })}
          </h2>
          <p className="text-xs text-[#7A6455] mt-0.5">{schedule.length} créneaux — Aperçu du planning type</p>
        </div>

        {/* Schedule (locked) */}
        <div className="space-y-3 relative">
          {schedule.map((cls, idx) => (
            <Card key={idx} className="bg-white border border-[#E9DDCC] rounded-2xl overflow-hidden opacity-75">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Time */}
                    <div className="flex-shrink-0 text-center min-w-[80px]">
                      <p className="text-xl font-heading font-light text-[#493329]">{cls.time}</p>
                      <p className="text-xs text-[#7A6455]">{cls.end}</p>
                    </div>
                    <div className="w-px h-12 bg-[#E9DDCC] flex-shrink-0" />
                    {/* Info */}
                    <div className="flex-1">
                      <h3 className="font-heading font-medium text-[#493329] text-sm mb-1">{cls.name}</h3>
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-[#C99A55]" strokeWidth={1.5} />
                        <span className="text-sm text-[#493329]">0/{cls.spots}</span>
                        <span className="text-xs text-[#7A6455]">réservé</span>
                      </div>
                    </div>
                  </div>
                  {/* Locked button */}
                  <div className="flex-shrink-0">
                    <Button disabled className="rounded-full px-5 h-9 text-sm bg-[#E9DDCC] text-[#AE9885] cursor-not-allowed">
                      <Lock className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Fermé
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Schedule recap */}
        <div className="mt-10 text-center">
          <p className="text-xs text-[#AE9885] uppercase tracking-widest mb-2">Horaires prévus</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-[#7A6455]">
            <span>Lun — Ven : 09h - 11h, 18h - 20h</span>
            <span>Sam : 09h - 13h</span>
          </div>
          <p className="text-xs text-[#AE9885] italic mt-4">Ouverture anticipée probable.</p>
        </div>
      </div>
    </div>
  );
}
